import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { apiError } from "../lib/api";
import { Skull, ShieldCheck } from "lucide-react";

const HERO = "https://images.pexels.com/photos/20993079/pexels-photo-20993079.jpeg";

export default function Login() {
  const { login, googleLogin } = useAuth();
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password, mfaRequired ? mfaCode : undefined);
      navigate("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "MFA_REQUIRED") {
        setMfaRequired(true);
        setError("");
      } else {
        setError(apiError(detail) || err.message);
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="concert" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <Skull className="text-primary mb-4" size={48} />
          <h1 className="heading text-5xl font-bold text-white">MetalVault</h1>
          <p className="text-zinc-300 mt-3 text-lg max-w-md">{t("tagline")}</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 pb-28 sm:pb-6">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-2 lg:hidden">
              <Skull className="text-primary" size={28} />
              <span className="heading text-2xl font-bold text-white">MetalVault</span>
            </div>
            <button onClick={toggle} className="ml-auto text-xs label-caps text-zinc-400 hover:text-white" data-testid="lang-toggle">
              {lang.toUpperCase()}
            </button>
          </div>

          <h2 className="heading text-3xl font-bold text-white mb-6">{t("login")}</h2>

          {error && <div data-testid="login-error" className="mb-4 text-sm text-red-400 border border-red-900 bg-red-950/40 px-3 py-2 rounded-sm">{error}</div>}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label-caps text-xs text-zinc-400">{t("email")}</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full bg-black border border-zinc-800 rounded-sm px-3 py-2.5 text-white focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="label-caps text-xs text-zinc-400">{t("password")}</label>
              <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full bg-black border border-zinc-800 rounded-sm px-3 py-2.5 text-white focus:border-primary focus:outline-none" />
            </div>
            {mfaRequired && (
              <div>
                <label className="label-caps text-xs text-primary flex items-center gap-1"><ShieldCheck size={14} /> {t("mfa_code_label")}</label>
                <input data-testid="login-mfa-code" autoFocus value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} placeholder={t("enter_code")}
                  className="mt-1 w-full bg-black border border-primary/50 rounded-sm px-3 py-2.5 text-white focus:border-primary focus:outline-none tracking-widest" />
              </div>
            )}
            <button data-testid="login-submit" disabled={loading} type="submit"
              className="w-full bg-primary text-primary-foreground label-caps py-3 rounded-sm font-semibold hover:bg-yellow-400 transition-colors disabled:opacity-60">
              {loading ? "..." : t("login")}
            </button>
          </form>

          <p className="mt-3 text-sm text-right">
            <Link to="/forgot" data-testid="forgot-link" className="text-zinc-400 hover:text-primary">{t("forgot_password")}</Link>
          </p>

          <div className="my-6 flex items-center gap-3 text-zinc-600 text-xs label-caps">
            <div className="flex-1 h-px bg-zinc-800" /> OR <div className="flex-1 h-px bg-zinc-800" />
          </div>

          <button data-testid="google-login" onClick={googleLogin}
            className="w-full border border-zinc-700 text-white py-3 rounded-sm hover:bg-zinc-900 transition-colors flex items-center justify-center gap-2">
            <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="g" className="w-5 h-5" />
            {t("google_login")}
          </button>

          <p className="mt-6 text-sm text-zinc-400">
            {t("no_account")}{" "}
            <Link to="/register" data-testid="go-register" className="text-primary hover:underline">{t("register")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
